import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui/form";
import { Label } from "@/ui/label";
import { RadioGroup, RadioGroupItem } from "@/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/ui/tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, ChevronLeft, Info } from "lucide-react";
import { useForm } from "react-hook-form";
import { useMemo } from "react";
import { useAppDispatch, useAppSelector } from '@/app/reduxHooks';
import { useSourceData } from "@/features/adp/hooks/useSourceData";
import { useTargetData } from "@/features/adp/hooks/useTargetData";
import { useBulkUpdateSourceFileFieldsMutation } from "@/features/adp/mutations/useSourceMutations";
import {
    applyIncrementalIdentifiersToSourceFiles,
    buildIncrementalBulkUpdatePayload,
    getUpstreamSourceOriginalFieldNames,
} from "@/features/adp/components/destination/dialogs/load-type/loadType.utils";
import { closeOverlayAsync } from "@/features/adp/state/overlay.thunks";
import { openOverlay, type LoadTypeOverlayData } from "@/features/adp/state/overlaySlice";
import { Button } from "@/ui/button";
import LoadTypeIcon from '@/assets/icons/cdc_type.svg?react';
import { LoadTypeFormSchema, type LoadTypeFormData } from "@/features/adp/schemas/LoadType";
import styles from './LoadType.module.scss';
import { LOAD_TYPE_PREREQUISITES } from "./loadType.prerequisite";
import PrerequisiteDialog from "../../../dialogs/PrerequisiteDialog";

const LoadType = () => {
    const { type, isOpen } = useAppSelector((state) => state.overlay);
    const isOpenForLoadType = isOpen && type === 'load-type';
    const loadTypeOverlayData = useAppSelector((state) =>
        state.overlay.type === "load-type" && state.overlay.isOpen
            ? (state.overlay.data as LoadTypeOverlayData)
            : null,
    );
    const dispatch = useAppDispatch();
    const { getSourceDataByNodeId, updateSourceData } = useSourceData();
    const { updateTargetData, getTargetDataByNodeId } = useTargetData();
    const bulkUpdateSourceFileFieldsMutation = useBulkUpdateSourceFileFieldsMutation();

    const nodeId = loadTypeOverlayData?.nodeId ?? null;
    const sourceColumnOptions = useMemo(() => {
        if (!isOpenForLoadType || nodeId == null) return [];
        return getUpstreamSourceOriginalFieldNames(nodeId, getSourceDataByNodeId);
    }, [isOpenForLoadType, nodeId, getSourceDataByNodeId]);

    const form = useForm<LoadTypeFormData>({
        resolver: zodResolver(LoadTypeFormSchema),
        mode: "onChange",
        defaultValues: {
            loadType: "full",
            scdType: "SCD1",
            deleteIdentifier: "",
            createdDate: "",
            modifiedDate: "",
        },
    });

    const loadType = form.watch("loadType");
    const selectedDelete = form.watch("deleteIdentifier");
    const selectedCreated = form.watch("createdDate");
    const selectedModified = form.watch("modifiedDate");

    const deleteOptions = sourceColumnOptions.filter(
        (column) => column.fieldName !== selectedCreated && column.fieldName !== selectedModified
    );

    const createdOptions = sourceColumnOptions.filter(
        (column) => column.fieldName !== selectedDelete && column.fieldName !== selectedModified
    );

    const modifiedOptions = sourceColumnOptions.filter(
        (column) => column.fieldName !== selectedDelete && column.fieldName !== selectedCreated
    );
    const isIncrementalSelectionComplete =
        loadType !== "incremental" ||
        (Boolean(selectedDelete) && Boolean(selectedCreated) && Boolean(selectedModified));

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            dispatch(closeOverlayAsync());
            form.reset();
        }
    };

    const handleSubmit = async (values: LoadTypeFormData) => {
        if (nodeId == null) return;

        updateTargetData(nodeId, { loadTypeForm: values });

        if (values.loadType === "incremental") {
            const payload = buildIncrementalBulkUpdatePayload(values, sourceColumnOptions);
            await bulkUpdateSourceFileFieldsMutation.mutateAsync(payload);

            const sourceNodeId = sourceColumnOptions[0]?.sourceNodeId;
            if (sourceNodeId) {
                const sourceData = getSourceDataByNodeId(sourceNodeId)?.data;
                const sourceFiles = Array.isArray(sourceData?.source_files) ? sourceData.source_files : [];
                const updatedSourceFiles = applyIncrementalIdentifiersToSourceFiles(sourceFiles, payload);
                updateSourceData(sourceNodeId, { source_files: updatedSourceFiles });
            }
        }

        const stored = getTargetDataByNodeId(nodeId)?.data;
        dispatch(openOverlay({
            type: "output-preview",
            data: {
                nodeId,
                data_source_instance: (stored?.data_source_instance as string | undefined) ?? null,
                data_source_type: (stored?.data_source_type as string | undefined) ?? null,
            },
        }));
    };

    return (
        <Dialog open={isOpenForLoadType} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader className="flex !flex-row items-center gap-2 justify-start">
                    <DialogClose asChild>
                        <Button variant="ghost" size="icon">
                            <ChevronLeft />
                        </Button>
                    </DialogClose>
                    <DialogTitle className="text-xl font-medium !mt-0">Load Type</DialogTitle>
                </DialogHeader>

                <DialogBody>
                    <div className="w-full">
                        <div className="mb-6 flex items-center gap-4">
                            <div className={styles.iconContainer}>
                                <LoadTypeIcon className="size-[56px]" />
                            </div>
                            <div>
                                <p className="font-medium">CDC Type</p>
                                <PrerequisiteDialog type="cdc" prerequisite={LOAD_TYPE_PREREQUISITES}>
                                    <Button type="button" variant="text">
                                        <AlertTriangle />
                                        Prerequisites
                                    </Button>
                                </PrerequisiteDialog>
                            </div>
                        </div>

                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                                <FormField
                                    control={form.control}
                                    name="loadType"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="hidden">CDC Type</FormLabel>
                                            <FormControl>
                                                <RadioGroup
                                                    value={field.value}
                                                    onValueChange={field.onChange}
                                                    orientation="horizontal"
                                                    className={`flex flex-row items-center gap-8 ${styles.radio_group}`}
                                                >
                                                    <div className={styles.radio}>
                                                        <RadioGroupItem value="full" id="load-type-full" className={styles.btn_radio} />
                                                        <Label htmlFor="load-type-full" className={styles.label_radio}>
                                                            Full Load
                                                        </Label>
                                                    </div>
                                                    <div className={styles.radio}>
                                                        <RadioGroupItem value="incremental" id="load-type-incremental" className={styles.btn_radio} />
                                                        <Label htmlFor="load-type-incremental" className={styles.label_radio}>
                                                            Incremental Load
                                                        </Label>
                                                    </div>
                                                </RadioGroup>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />

                                <div className={loadType === "incremental" ? "grid grid-cols-2 gap-4" : ""}>
                                    <FormField
                                        control={form.control}
                                        name="scdType"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="flex items-center gap-1">
                                                    SCD Type
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Info className="h-4 w-4 cursor-pointer text-gray-500" />
                                                        </TooltipTrigger>
                                                        <TooltipContent className="max-w-xs">
                                                            Slowly Changing Dimension (SCD) keeps track of both current and historical data.
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </FormLabel>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    value={field.value}
                                                    disabled={loadType === "full"}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select SCD Type" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        <SelectItem value="SCD1">SCD1</SelectItem>
                                                        <SelectItem value="SCD2">SCD2</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {loadType === "incremental" && (
                                        <>
                                            <FormField
                                                control={form.control}
                                                name="deleteIdentifier"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel className="flex items-center gap-1">
                                                            Delete Identifier
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Info className="h-4 w-4 cursor-pointer text-gray-500" />
                                                                </TooltipTrigger>
                                                                <TooltipContent className="max-w-[220px]">
                                                                    Select column which identifies delete operations.
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </FormLabel>
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <FormControl>
                                                                <SelectTrigger>
                                                                    <SelectValue placeholder="Select delete identifier column" />
                                                                </SelectTrigger>
                                                            </FormControl>
                                                            <SelectContent>
                                                                {deleteOptions.map((column) => (
                                                                    <SelectItem
                                                                        key={`${column.sourceFileId}-${column.sourceFileFieldId}`}
                                                                        value={column.fieldName}
                                                                    >
                                                                        {column.fieldName}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="createdDate"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel className="flex items-center gap-1">
                                                            Created Date
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Info className="h-4 w-4 cursor-pointer text-gray-500" />
                                                                </TooltipTrigger>
                                                                <TooltipContent className="max-w-[200px]">
                                                                    Select column where created datetime exists.
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </FormLabel>
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <FormControl>
                                                                <SelectTrigger>
                                                                    <SelectValue placeholder="Select created datetime" />
                                                                </SelectTrigger>
                                                            </FormControl>
                                                            <SelectContent>
                                                                {createdOptions.map((column) => (
                                                                    <SelectItem
                                                                        key={`${column.sourceFileId}-${column.sourceFileFieldId}`}
                                                                        value={column.fieldName}
                                                                    >
                                                                        {column.fieldName}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="modifiedDate"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel className="flex items-center gap-1">
                                                            Modified Date
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Info className="h-4 w-4 cursor-pointer text-gray-500" />
                                                                </TooltipTrigger>
                                                                <TooltipContent className="max-w-[250px]">
                                                                    Select column where modified datetime exists.
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </FormLabel>
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <FormControl>
                                                                <SelectTrigger>
                                                                    <SelectValue placeholder="Select modified datetime" />
                                                                </SelectTrigger>
                                                            </FormControl>
                                                            <SelectContent>
                                                                {modifiedOptions.map((column) => (
                                                                    <SelectItem
                                                                        key={`${column.sourceFileId}-${column.sourceFileFieldId}`}
                                                                        value={column.fieldName}
                                                                    >
                                                                        {column.fieldName}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </>
                                    )}
                                </div>

                                <DialogFooter className="px-0 pb-0">
                                    <DialogClose asChild>
                                        <Button type="button" variant="outline">
                                            Cancel
                                        </Button>
                                    </DialogClose>
                                    <Button
                                        type="submit"
                                        disabled={
                                            !form.formState.isValid ||
                                            !isIncrementalSelectionComplete ||
                                            bulkUpdateSourceFileFieldsMutation.isPending
                                        }
                                    >
                                        {bulkUpdateSourceFileFieldsMutation.isPending ? "Saving..." : "Save"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </Form>
                    </div>
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
};

export default LoadType;